/**
 * Se persiste desde el PNR. Si ancillaries no lo trae, se inyecta
 * Palabra: CLASSIC | GOLD | SILVER | PLATINUM | TITANIUM | EAGLE KNIGHT
 * Codigo PNR (CLA, SLV/ELT, TTP, etc) se convierte a esa palabra
 */

export type FfTierSource = Record<string, unknown> | null | undefined;

const FF_TIER_WORDS = new Set([
  'CLASSIC',
  'GOLD',
  'SILVER',
  'PLATINUM',
  'TITANIUM',
  'EAGLE KNIGHT',
]);

const PNR_CODE_TO_FF_TIER: Record<string, string> = {
  CLA: 'CLASSIC',
  GLD: 'GOLD',
  'GLD/ELT': 'GOLD',
  SLV: 'SILVER',
  'SLV/ELT': 'SILVER',
  PLT: 'PLATINUM',
  'PLT/ELP': 'PLATINUM',
  TTN: 'TITANIUM',
  'TTN/ELP': 'TITANIUM',
  TTP: 'EAGLE KNIGHT',
  'TTP/ELP': 'EAGLE KNIGHT',
};

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

export function normalizeFfTier(value: unknown): string {
  return isNonEmptyString(value) ? value.trim() : '';
}

export function hasFfTier(value: unknown): boolean {
  return normalizeFfTier(value).length > 0;
}

/** Palabra de redencion o codigo PNR mapeado */
export function mapFfTier(value: unknown): string {
  const raw = normalizeFfTier(value);
  if (!raw) {
    return '';
  }
  const upper = raw.toUpperCase();
  if (FF_TIER_WORDS.has(upper)) {
    return upper;
  }
  return PNR_CODE_TO_FF_TIER[upper] || '';
}

/**
 * 1) Respuesta de ancillaries si trae valor
 * 2) si no, ffName del PNR
 * 3) si no, ffTierLevel del PNR (codigo → palabra)
 */
export function resolveFfTier(
  fromAncillaries?: unknown,
  fromFfName?: unknown,
  fromPnr?: unknown
): string {
  const fromResponse = mapFfTier(fromAncillaries);
  if (fromResponse) {
    return fromResponse;
  }
  const fromName = mapFfTier(fromFfName);
  if (fromName) {
    return fromName;
  }
  return mapFfTier(fromPnr);
}

export function readFfTier(source: FfTierSource): string {
  if (!source || typeof source !== 'object') {
    return '';
  }
  return normalizeFfTier(source['ffTierLevel']);
}

function travelerNameNumber(traveler: Record<string, unknown>): string {
  const raw =
    traveler['nameNumber'] ?? traveler['nameRefNumber'] ?? traveler['passengerNameNumber'] ?? '';
  return typeof raw === 'string' ? raw : '';
}

/** Extrae ffTierLevel de la respuesta de ancillaries */
export function extractFfTier(response: unknown, nameNumber?: string): string {
  if (!response || typeof response !== 'object') {
    return '';
  }

  const payload = response as Record<string, unknown>;
  const travelers = Array.isArray(payload['travelerInfo'])
    ? (payload['travelerInfo'] as Record<string, unknown>[])
    : Array.isArray(payload['passengers'])
    ? (payload['passengers'] as Record<string, unknown>[])
    : [];

  if (travelers.length > 0) {
    const match = nameNumber
      ? travelers.find((traveler) => travelerNameNumber(traveler) === nameNumber)
      : travelers[0];
    const fromTraveler = readFfTier(match);
    if (fromTraveler) {
      return fromTraveler;
    }
  }

  return readFfTier(payload);
}

/** Forma mínima que usa applyRefreshedFfTier; Passenger la cumple. */
export interface FfTierHolder {
  ffTierLevel?: string;
  passengerInfo?: { ffNumber?: string; ffProgram?: string; ffTierLevel?: string } | null;
}

const normalizeFfProgram = (value: unknown): string => normalizeFfTier(value).toUpperCase();
const normalizeFfNumber = (value: unknown): string =>
  normalizeFfProgram(value).replace(/^0+/, '');

/**
 * CVT20-14802: al asociar el FFN en kiosko, el PNR refrescado ya trae el nivel
 * (TTP, TTP/ELP...). Se conserva en `ffTierLevel` del pasajero (fuente de
 * resolvePassengerFfTier) solo si es el mismo FFN y programa. No toca passengerInfo.
 */
export function applyRefreshedFfTier(target: FfTierHolder, refreshed: FfTierHolder): boolean {
  if (!target?.passengerInfo || !refreshed?.passengerInfo) {
    return false;
  }
  const tier = normalizeFfTier(refreshed.passengerInfo.ffTierLevel);
  const ffNumber = normalizeFfNumber(target.passengerInfo.ffNumber);
  const ffProgram = normalizeFfProgram(target.passengerInfo.ffProgram);
  const isSameFfn = !!ffNumber && ffNumber === normalizeFfNumber(refreshed.passengerInfo.ffNumber);
  const isSameProgram =
    !!ffProgram && ffProgram === normalizeFfProgram(refreshed.passengerInfo.ffProgram);
  if (!tier || !isSameFfn || !isSameProgram) {
    return false;
  }
  target.ffTierLevel = tier;
  return true;
}
